import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listVendors } from '@/api/catalog';
import { StarRating } from '@/components/StarRating';
import { VendorLogo } from '@/components/VendorLogo';
import { vendorStorePath } from '@/lib/vendorUrl';

function VendorMiniCard({ vendor }: { vendor: Awaited<ReturnType<typeof listVendors>>[number] }) {
  return (
    <Link
      to={vendorStorePath(vendor)}
      className="flex w-[200px] shrink-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 shadow-sm transition hover:border-brand-300 dark:border-slate-700 dark:bg-slate-900"
    >
      <VendorLogo vendor={vendor} size="sm" />
      <div className="min-w-0">
        <p className="truncate text-[13px] font-medium text-slate-900 dark:text-white">
          {vendor.businessName}
        </p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          {vendor.pickupParish ?? 'Grenada'}
        </p>
        {vendor.ratingCount > 0 ? (
          <div className="mt-0.5">
            <StarRating rating={vendor.ratingAvg} count={vendor.ratingCount} />
          </div>
        ) : null}
      </div>
    </Link>
  );
}

export function FeaturedBusinessesStrip() {
  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ['home', 'featuredVendors'],
    queryFn: async () => {
      const all = await listVendors();
      return all
        .filter((v) => v.verified && (v.approvalStatus ?? 'none') === 'approved')
        .sort((a, b) => b.ratingAvg - a.ratingAvg || b.ratingCount - a.ratingCount)
        .slice(0, 12);
    },
    staleTime: 5 * 60_000,
  });

  if (!isLoading && vendors.length === 0) return null;

  // Duplicate the row so the CSS loop can translate -50% seamlessly.
  const loop = vendors.length > 0 ? [...vendors, ...vendors] : [];

  return (
    <section className="border-t border-slate-200 bg-white py-10 dark:border-slate-800 dark:bg-slate-900">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        <div className="mb-5 flex items-end justify-between gap-4">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Featured businesses</h2>
          <Link
            to="/grenada/vendors"
            className="shrink-0 text-sm font-semibold text-brand-700 hover:underline dark:text-brand-400"
          >
            View all →
          </Link>
        </div>

        {isLoading ? (
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 4 }, (_, i) => (
              <div
                key={i}
                className="h-[72px] w-[200px] shrink-0 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : (
          <div className="featured-biz-marquee-wrap relative -mx-4 overflow-hidden sm:-mx-6">
            <div
              className="featured-biz-marquee flex w-max gap-3 px-4 sm:px-6"
              style={{
                animationDuration: `${Math.max(28, vendors.length * 5)}s`,
              }}
            >
              {loop.map((v, i) => (
                <VendorMiniCard key={`${v.id}-${i}`} vendor={v} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
